/**
 * Hook for pagination logic and calculations.
 * Provides common pagination functionality that can be reused across different components.
 */
export interface UsePaginationProps {
  totalItems: number
  currentPage: number
  pageSize: number
  pageSizeOptions?: number[]
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
}

export interface UsePaginationReturn {
  // State
  currentPage: number
  pageSize: number

  // Calculations
  totalPages: number
  startRow: number
  endRow: number

  // Navigation state
  canGoPrevious: boolean
  canGoNext: boolean

  // Navigation actions
  goToFirst: () => void
  goToPrevious: () => void
  goToNext: () => void
  goToLast: () => void
  handlePageChange: (page: number) => void

  // Page size handling
  handlePageSizeChange: (newPageSize: number) => void

  // Validations
  isValidPage: (page: number) => boolean
  isValidPageSize: (size: number) => boolean
}

export const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 30, 40, 50, 100]

/**
 * Hook that provides common pagination logic and calculations.
 * Can be used by any component that needs pagination functionality.
 */
export function usePagination({
  totalItems,
  currentPage,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
}: UsePaginationProps): UsePaginationReturn {
  // Calculations
  const totalPages = Math.ceil(totalItems / pageSize)
  const startRow = (currentPage - 1) * pageSize + 1
  const endRow = Math.min(currentPage * pageSize, totalItems)

  // Navigation state
  const canGoPrevious = currentPage > 1
  const canGoNext = currentPage < totalPages

  // Navigation actions
  const goToFirst = () => {
    onPageChange?.(1)
  }
  const goToPrevious = () => {
    const newPage = currentPage - 1
    onPageChange?.(newPage)
  }
  const goToNext = () => {
    const newPage = currentPage + 1
    onPageChange?.(newPage)
  }
  const goToLast = () => {
    onPageChange?.(totalPages)
  }

  // Generic page change handler
  const handlePageChange = (page: number) => {
    if (isValidPage(page)) {
      onPageChange?.(page)
    }
  }

  // Page size change handler
  const handlePageSizeChange = (newPageSize: number) => {
    if (isValidPageSize(newPageSize)) {
      onPageSizeChange?.(newPageSize)
    }
  }

  // Validations
  const isValidPage = (page: number) => page >= 1 && page <= totalPages
  const isValidPageSize = (size: number) => pageSizeOptions.includes(size)

  return {
    // State
    currentPage,
    pageSize,

    // Calculations
    totalPages,
    startRow,
    endRow,

    // Navigation state
    canGoPrevious,
    canGoNext,

    // Navigation actions
    goToFirst,
    goToPrevious,
    goToNext,
    goToLast,
    handlePageChange,

    // Page size handling
    handlePageSizeChange,

    // Validations
    isValidPage,
    isValidPageSize,
  }
}
